import { useState, useCallback, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNotifications } from '../context/NotificationContext';

const NOTIFICATION_COUNT_KEY = 'challengeNotificationCount';
const CHAT_NOTIFICATION_COUNT_KEY = 'chatNotificationCount';

interface NotificationCountHook {
  notificationCount: number;
  chatCount: number;
  isLoading: boolean;
  updateNotificationCount: (count: number) => Promise<void>;
  updateChatCount: (count: number) => Promise<void>;
  resetNotificationCount: () => Promise<void>;
  resetChatCount: () => Promise<void>;
  incrementNotificationCount: () => Promise<void>;
  incrementChatCount: () => Promise<void>;
}

export const useNotificationCount = (): NotificationCountHook => {
  const { newNotificationCount, setNewNotificationCount } = useNotifications();
  const [chatCount, setChatCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize counts from AsyncStorage on first load
  useEffect(() => {
    const initializeCounts = async () => {
      if (isInitialized) return;
      
      setIsLoading(true);
      try {
        const [storedNotificationCount, storedChatCount] = await Promise.all([
          AsyncStorage.getItem(NOTIFICATION_COUNT_KEY),
          AsyncStorage.getItem(CHAT_NOTIFICATION_COUNT_KEY)
        ]);

        if (storedNotificationCount) {
          const count = parseInt(storedNotificationCount, 10);
          if (!isNaN(count) && count !== newNotificationCount) {
            setNewNotificationCount(count);
          }
        }

        if (storedChatCount) {
          const count = parseInt(storedChatCount, 10);
          if (!isNaN(count)) {
            setChatCount(count);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize notification counts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCounts();
  }, [isInitialized, newNotificationCount, setNewNotificationCount]);

  // Atomic update function to prevent race conditions
  const atomicUpdate = useCallback(async (
    key: string, 
    count: number, 
    setStateCallback: (count: number) => void
  ): Promise<void> => {
    setIsLoading(true);
    try {
      // Use a semaphore-like approach to prevent concurrent updates
      const lockKey = `${key}_lock`;
      const lockValue = Date.now().toString();
      
      // Try to acquire lock
      const existingLock = await AsyncStorage.getItem(lockKey);
      if (existingLock && (Date.now() - parseInt(existingLock)) < 5000) {
        // Lock exists and is recent, wait a bit and retry
        await new Promise(resolve => setTimeout(resolve, 100));
        return atomicUpdate(key, count, setStateCallback);
      }

      // Acquire lock
      await AsyncStorage.setItem(lockKey, lockValue);

      // Update the count
      await AsyncStorage.setItem(key, count.toString());
      setStateCallback(count);

      // Release lock
      await AsyncStorage.removeItem(lockKey);
    } catch (error) {
      console.error(`Failed to update ${key}:`, error);
      // Attempt to release lock on error
      try {
        await AsyncStorage.removeItem(`${key}_lock`);
      } catch (lockError) {
        console.error('Failed to release lock:', lockError);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateNotificationCount = useCallback(async (count: number): Promise<void> => {
    await atomicUpdate(NOTIFICATION_COUNT_KEY, count, setNewNotificationCount);
  }, [atomicUpdate, setNewNotificationCount]);

  const updateChatCount = useCallback(async (count: number): Promise<void> => {
    await atomicUpdate(CHAT_NOTIFICATION_COUNT_KEY, count, setChatCount);
  }, [atomicUpdate]);

  const resetNotificationCount = useCallback(async (): Promise<void> => {
    try {
      await Promise.all([
        AsyncStorage.removeItem(NOTIFICATION_COUNT_KEY),
        AsyncStorage.removeItem(`${NOTIFICATION_COUNT_KEY}_lock`)
      ]);
      setNewNotificationCount(0);
    } catch (error) {
      console.error('Failed to reset notification count:', error);
      throw error;
    }
  }, [setNewNotificationCount]);

  const resetChatCount = useCallback(async (): Promise<void> => {
    try {
      await Promise.all([
        AsyncStorage.removeItem(CHAT_NOTIFICATION_COUNT_KEY),
        AsyncStorage.removeItem(`${CHAT_NOTIFICATION_COUNT_KEY}_lock`)
      ]);
      setChatCount(0);
    } catch (error) {
      console.error('Failed to reset chat count:', error);
      throw error;
    }
  }, []);

  const incrementNotificationCount = useCallback(async (): Promise<void> => {
    const newCount = newNotificationCount + 1;
    await updateNotificationCount(newCount);
  }, [newNotificationCount, updateNotificationCount]);

  const incrementChatCount = useCallback(async (): Promise<void> => {
    const newCount = chatCount + 1;
    await updateChatCount(newCount);
  }, [chatCount, updateChatCount]);

  return {
    notificationCount: newNotificationCount,
    chatCount,
    isLoading,
    updateNotificationCount,
    updateChatCount,
    resetNotificationCount,
    resetChatCount,
    incrementNotificationCount,
    incrementChatCount,
  };
};

export default useNotificationCount;
